import React, { useState, useContext, useEffect } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import selectors from '@state/selectors';
import {IProps, IState} from './types';
import * as S from './styles';
import C from '@assets/themes/colors';
import { Context } from "routes/private/Editor/Context";
import Search from '@routes/private/BrandLibrary/components/Search';
import Filter from '@routes/private/BrandLibrary/components/Filter';
import { FilterIcon } from '@assets/icons/filterIcon';
import Button from '@components/Button';
import Swipeable from '@components/Swipeable';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { storageAccountName, containerNameBrands, containerName } from '@storage/azure-storage-blob';
import { sendFile, copyFile } from '@utils/cloudStorage';
import { handleImageError, handleBrandImageLoad, getOEMDefault } from '@utils/brand';
import { IBrandImage, IBrandLogo } from '@state/brandsImages/types';
import { IDSConfig, IBrandDefinition, IBrandImageType } from '@state/dsconfig/types';
import { IPickerSearchImagesProperties, IElement, IImageAttributes} from "routes/private/Editor/types";
import actions from '@state/actions';
import Loading from '@components/Loading';
import { getWidthHeightFromSrc } from '@utils/userUtils';
import D from '@assets/themes/dimensions';
import DragElement from '../../DragElement';
import _ from 'lodash';

const uuid = require('react-uuid');

const BrandSection:React.FC<IProps> = (props) => {
  let {entityInformation, editorStatus, data, userFiles, parsedTreeInfo, onCommandChange} = useContext(Context);
  const [imagesList, setImagesList] = useState<IBrandImage[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);  
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const urlBrand =`https://${storageAccountName}.blob.core.windows.net/${containerNameBrands}`;
  const itemsLength = 5;
  const dispatch = useDispatch();
  const fetchImagesList: IBrandImage[] = useSelector(selectors.brandsImages.getBrandImagesSection);
  const dsConfig: IDSConfig = useSelector(selectors.dsConfig.getDSConfig);
  const handleViewAll=()=>{
    if (props.openSearch) props.openSearch(props.section);
  }
  let oemDefault: IBrandDefinition|undefined = getOEMDefault(entityInformation, dsConfig);

  const getImageSrc = (data:IBrandImage)=>{
    
    if (data.id=="" || data.id== undefined)
      return data.srcImage;
    else
    {      
      return `${urlBrand}/${data.brandId}/${data.imageType?data.imageType.toLowerCase():""}/${data.srcImage}` + "TN";
    }
  }

  const fetchImages = () => {
    setIsLoading(true);
    
    let pickerProperties : IPickerSearchImagesProperties = (editorStatus.pickerProperties as IPickerSearchImagesProperties);
    let searchQuery = ( pickerProperties?.search.length>0)  ? `and udf.searchMetadataBrandImage("${pickerProperties?.search}", c)` : '';
    let brandQuery = (pickerProperties?.search.length>0)  ? `` : `and c.brandId IN ('${oemDefault?.id}')`;
    let query = `c.status='ACTIVE' and c.imageType='${props.section.toLocaleUpperCase()}' ${brandQuery} ${searchQuery}&orderby=c.updateDate DESC`;
    return new Promise((resolve, reject) => {      
      dispatch(actions.brandsImages.fetchBrandImages(query, 0, 5, 'section',  (result, error) => {      
        if (error) reject(error)
        else {  
          setIsLoading(false);        
          resolve(result)}
      }))
    });
  }

  useEffect(() => {
    
    if (props.section || editorStatus.pickerProperties) {
      setImagesList([]);
      fetchImages();
    }
  }, [props.section, editorStatus.pickerProperties]);

  useEffect(() => {        
    if(fetchImagesList?.length>0 && fetchImagesList[0].imageType===props.section.toLocaleUpperCase()) {
      if (JSON.stringify(imagesList.map(img => {return img.id})) !== JSON.stringify(fetchImagesList.map(img => {return img.id}))) {
        setImagesList(_.cloneDeep(fetchImagesList));
      }
    }
          
  }, [fetchImagesList]);

  const handleOnSelect = async(imageInfo: any, e: any) => {
    let srcFileOrg= `${urlBrand}/${imageInfo.brandId}/${imageInfo.imageType?imageInfo.imageType.toLowerCase():""}/${imageInfo.srcImage}`;
    const dimensionsOrg = await getWidthHeightFromSrc(srcFileOrg ?? "");
    if (dimensionsOrg) {
      imageInfo.initWidth = dimensionsOrg.width;
      imageInfo.initHeight = dimensionsOrg.height;
    }
    if (props.onSelect) props.onSelect(imageInfo);
    setSelectedIndex(undefined);
  }
  
  return (
    <S.Root>
      <S.SectionContainer>
        <S.HeaderContainer>
            <S.HeaderTitle>
            {props.title}
            </S.HeaderTitle>
            {(!isLoading && !editorStatus.pickerProperties) &&
            <S.HeaderButton>
            <Button
                label={imagesList.length>0 ? `VIEW ALL`:`BROWSE`}        
                onClick={(e?:any)=>{
                  e.stopPropagation();
                  handleViewAll()
                }}
                style={{
                  position: `absolute`,
                  right: `-9px`,
                  top: `-25px`,
                  backgroundColor: `#00000000`,
                  hoverBackgroundColor: `#e1e3e9`,
                  padding: `8px 9px 8px 9px`,
                  boxShadow: `0px 0px 0px 0 rgba(0, 0, 0, 0)`,
                }}
                labelStyle={{
                  color: `${C.DARK_BLUE}`,
                }}
            />
            </S.HeaderButton>}
        </S.HeaderContainer>
        {imagesList.length==0 ?
        <S.BodyContainer>
          <S.EmptyState>
            {isLoading ?<Loading/> : oemDefault?.useAsCommon && !editorStatus.pickerProperties ? `Browse to add ${props.section.charAt(0).toLocaleUpperCase()}${props.section.slice(1, -1)} Images` : 'No Results'}
          </S.EmptyState>
        </S.BodyContainer>
        :
        <S.SwipeableContainer>
          <Swipeable
            itemsLength={itemsLength}
            itemWidth={D.BRAND_ITEM_WIDTH + 10}
            topArrows={40}                 
            itemsCount={imagesList.length}   
            itemsShow={1}
            id={`${props.section}0`}
            smallArrows={true}
            centerItem={true}
          >
            <TransitionGroup className="group-list">
              { imagesList.map((imageInfo: any, index: number) => {
                  return (
                    <CSSTransition key={imageInfo.id} timeout={600} classNames="item">
                      <DragElement
                        key={index}
                        id={imageInfo.id}
                        type="Image"
                        itemData={imageInfo}
                        style={{padding: `0px 5px 0px 5px`}}
                      >
                        <S.ItemContainer 
                          id={`preview-component-${index}-${props.section}0`}
                          selected={false}
                          onClick={(e) => {handleOnSelect(imageInfo, e)}}
                        >
                          <S.ImgContainer
                            width={imageInfo.width}
                            height={imageInfo.height}
                            src={getImageSrc(imageInfo)} 
                            onClick={()=>{setSelectedIndex(index);}} 
                            transparent={props.section!=='vehicles' ? true : imageInfo.type==="Jellybean"}       
                            onLoad={(e) => setImagesList(handleBrandImageLoad(e, imageInfo.id, imagesList))}
                            onError={() => setImagesList(handleImageError(imageInfo.id, imagesList))}
                          />
                        </S.ItemContainer>
                      </DragElement>
                    </CSSTransition>
                  )
                })
              }
              
            </TransitionGroup>
          </Swipeable>
        </S.SwipeableContainer>}
      </S.SectionContainer>        
    </S.Root>

  );
}

export default BrandSection;